import { ItemTypes } from '@/components/gantt/dragDrop/types';
import { BLANK_GUID, workingIdToGqlId } from '@/helpers/dailyHelper';
import { convertToUtcDate } from '@/helpers/dateHelper';
import {
  ReadTemplate,
  UpdateTemplate,
  UpdateTemplatePosition,
  UpdateTemplatePositionTimeSlot,
  UpdateTemplateStation
} from '@/services/gql/graphql.generated';
import { useSystemSettings } from '@/services/settings/SystemSettingsProvider';
import { WorkingShift, WorkingShiftSubsection } from '@/types/daily.types';
import { WorkingRoster } from '@/types/roster.types';
import { secondsToHours } from 'date-fns';
import { useCallback } from 'react';
import { useBuildHourSummary } from '../roster/hooks/useHourSummary';

const parseTaskPropertiesContext = (propertiesString?: string | null) => {
  try {
    if (propertiesString) {
      const properties = JSON.parse(propertiesString);
      if (properties.type === ItemTypes.TASK) {
        return properties.context;
      }
    }
  } catch {
    return undefined;
  }

  return undefined;
};

const parseJsonObject = (value?: string | null) => {
  try {
    return value ? JSON.parse(value) : {};
  } catch {
    return {};
  }
};

export const useGqlTemplateToWorkingTemplate = () => {
  const buildHourSummary = useBuildHourSummary();
  const defaultEnd = useSystemSettings(state => state.settings.maxTime);
  const defaultStart = useSystemSettings(state => state.settings.minTime);

  return useCallback(
    (template: ReadTemplate) => {
      const templateStation = template.stations?.[0];
      if (!templateStation?.positions) {
        throw Error('Template stations are not configured correctly.');
      }

      const shifts: WorkingShift[] = templateStation.positions.map(position => {
        const breaks: WorkingShiftSubsection[] = (position?.breaks || []).map(brek => ({
          id: brek?.id || `CREATE-${crypto.randomUUID?.() || Math.random().toString(36).slice(2)}`,
          start: brek?.start || 0,
          end: brek?.end || 0,
          type: ItemTypes.BREAK
        }));

        const tasks: WorkingShiftSubsection[] = [];
        position?.timeSlots?.forEach(slot => {
          const context = parseTaskPropertiesContext(slot?.properties);
          if (context) {
            tasks.push({
              id: slot?.id || `CREATE-${crypto.randomUUID?.() || Math.random().toString(36).slice(2)}`,
              start: slot?.start || 0,
              end: slot?.end || 0,
              type: ItemTypes.TASK,
              typeContext: context
            });
          }
        });

        return {
          id: position?.id || `CREATE-${crypto.randomUUID?.() || Math.random().toString(36).slice(2)}`,
          start: position?.start,
          end: position?.end,
          assignedEmployeeId: position?.employeeId || undefined,
          assignedContractRoleId: position?.contractRoleId || undefined,
          breaks,
          tasks,
          properties: parseJsonObject(position?.properties),
          locationId: template.location?.id
        };
      });

      const properties = parseJsonObject(template.properties);

      let startTime = defaultStart;
      let endTime = defaultEnd;
      let openTime: number | undefined = undefined;
      let closeTime: number | undefined = undefined;

      if (template.shiftOperatingHours) {
        openTime = template.shiftOperatingHours.openTime ?? openTime;
        closeTime = template.shiftOperatingHours.closeTime ?? closeTime;
        startTime = template.shiftOperatingHours.startTime ?? startTime;
        endTime = template.shiftOperatingHours.endTime ?? endTime;
      }

      const scratchpadFromProperties = properties.scratchpad as number[] | undefined;
      const expectedLength = secondsToHours(endTime - startTime) * 2;
      const scratchpad =
        scratchpadFromProperties && scratchpadFromProperties.length === expectedLength
          ? scratchpadFromProperties
          : buildHourSummary(shifts, startTime, endTime);

      return {
        id: template.id || BLANK_GUID,
        shifts,
        scratchpad,
        date: new Date(0),
        startTime,
        endTime,
        openTime,
        closeTime,
        templateName: template.name,
        lastSaved: convertToUtcDate(template.lastModified)
      } satisfies WorkingRoster;
    },
    [buildHourSummary, defaultEnd, defaultStart]
  );
};

const toPositionTimeSlots = (shift: WorkingShift): UpdateTemplatePositionTimeSlot[] => {
  return shift.tasks.map(task => ({
    id: workingIdToGqlId(task.id),
    start: task.start,
    end: task.end,
    properties: JSON.stringify({ type: ItemTypes.TASK, context: task.typeContext })
  }));
};

const toBreaks = (shift: WorkingShift) => {
  return shift.breaks.map(brek => ({
    id: workingIdToGqlId(brek.id),
    start: brek.start,
    end: brek.end
  }));
};

export const buildTemplateUpdate = (workingTemplate: WorkingRoster, template: ReadTemplate): UpdateTemplate => {
  const existingProperties = parseJsonObject(template.properties);
  const mergedProperties = {
    ...existingProperties,
    scratchpad: workingTemplate.scratchpad
  };

  const templateStation = template.stations?.[0];

  const positions: UpdateTemplatePosition[] = workingTemplate.shifts.map((shift, index) => ({
    id: workingIdToGqlId(shift.id),
    displayOrder: index + 1,
    start: shift.start,
    end: shift.end,
    employeeId: shift.assignedEmployeeId || undefined,
    contractRoleId: shift.assignedContractRoleId || undefined,
    properties: JSON.stringify(shift.properties || {}),
    breaks: toBreaks(shift),
    timeSlots: toPositionTimeSlots(shift)
  }));

  const stations: UpdateTemplateStation[] | undefined = templateStation
    ? [
        {
          id: templateStation.id,
          stationId: templateStation.stationId,
          displayOrder: templateStation.displayOrder,
          positions
        }
      ]
    : undefined;

  return {
    id: template.id,
    name: template.name,
    locationId: template.location?.id,
    targetType: template.target?.type || '',
    targetValue: template.target?.value || 0,
    weekday: template.weekday,
    version: template.version || undefined,
    draft: template.draft || undefined,
    baseTemplateId: template.baseTemplateId || undefined,
    templateTags: template.templateTags?.map(tag => ({ label: tag?.label || '', templateId: tag?.id })) || undefined,
    properties: JSON.stringify(mergedProperties),
    shiftOperatingHours: {
      startTime: workingTemplate.startTime,
      endTime: workingTemplate.endTime,
      openTime: workingTemplate.openTime,
      closeTime: workingTemplate.closeTime
    },
    stations
  };
};
